<template>
    <div :class="listItemClasses">
        <div v-if="hasIcon" class="list-item__icon">
            <slot name="icon">
                <svg-icon
                    v-if="iconName"
                    :icon-name="iconName"
                    :width="iconWidth"
                    :height="iconHeight"
                    :color="iconColor"
                ></svg-icon>
            </slot>
        </div>

        <div v-if="hasTitle || hasSubtitle" class="list-item__content">
            <div v-if="hasTitle" class="list-item__title">
                <slot name="title">{{ title }}</slot>
            </div>
            <div v-if="hasSubtitle" class="list-item__subtitle">
                <slot name="subtitle">{{ subtitle }}</slot>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, useSlots } from 'vue';
    import SvgIcon from '@/components/base/SvgIcon.vue';

    type ListItemLayout = 'horizontal' | 'vertical';

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            iconName?: string;
            title?: string;
            subtitle?: string;
            layout?: ListItemLayout;
            iconWidth?: number;
            iconHeight?: number;
            iconColor?: string;
        }>(),
        {
            iconName: '',
            title: '',
            subtitle: '',
            layout: 'horizontal',
            iconWidth: 48,
            iconHeight: 48,
            iconColor: 'inherit',
        }
    );

    // ----- state -----
    const slots = useSlots();

    // ----- computed -----
    const hasIcon = computed(() => Boolean(slots.icon) || !!props.iconName);

    const hasTitle = computed(() => Boolean(slots.title) || !!props.title);

    const hasSubtitle = computed(
        () => Boolean(slots.subtitle) || !!props.subtitle
    );

    const listItemClasses = computed(() => [
        'list-item',
        `list-item--${props.layout}`,
    ]);
</script>

<style lang="scss" scoped>
    .list-item {
        display: flex;
        align-items: center;
        gap: 16px;
        max-width: 100%;

        &__icon {
            flex: 0 0 auto;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            line-height: 0;
        }

        &__content {
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        &__title,
        &__subtitle {
            max-width: 100%;
            word-break: break-word;
        }

        &__title {
            color: #454545;
            font-size: 18px;
            font-weight: 700;
        }

        &__subtitle {
            color: #7d7d7d;
            font-size: 16px;
        }

        &--vertical {
            flex-direction: column;
            justify-content: center;
            gap: 8px;
            text-align: center;

            .list-item__content {
                align-items: center;
                gap: 4px;
            }

            .list-item__title {
                font-size: 18px;
            }
        }
    }
</style>
